import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../contexts/ChatContext';
import Avatar from './Avatar';
import { FaTimes } from 'react-icons/fa';
import { IoSend } from 'react-icons/io5';
import './FloatingChat.css';

const FloatingChat = ({ user, onClose }) => {
  const { messages, sendMessage, createConversation, loadMessages, conversations } = useChat();
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  // Find or create conversation with this user
  useEffect(() => {
    if (!user) return;

    const existingConv = conversations.find(
      c => c.other_user && c.other_user.id === user.id
    );

    if (existingConv) {
      setConversationId(existingConv.id);
      loadMessages(existingConv.id);
    }
  }, [user, conversations, loadMessages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, conversationId]);

  const handleSend = async () => {
    if (!inputValue.trim() || sending) return;

    setSending(true);
    try {
      let convId = conversationId;

      // Create conversation if it doesn't exist
      if (!convId) {
        const newConv = await createConversation([user.id]);
        convId = newConv.id;
        setConversationId(convId);
      }

      await sendMessage(convId, inputValue.trim());
      setInputValue('');
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const conversationMessages = conversationId ? (messages[conversationId] || []) : [];

  return (
    <div className="floating-chat">
      <div className="floating-chat-header">
        <Avatar user={user} size="small" />
        <div className="floating-chat-header-info">
          <div className="floating-chat-header-name">
            {user.display_name || user.displayName || user.username}
          </div>
          <div className="floating-chat-header-status">Chatting</div>
        </div>
        <button className="floating-chat-close" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <div className="floating-chat-messages">
        {conversationMessages.length === 0 ? (
          <div className="floating-chat-empty">
            No messages yet. Say hello!
          </div>
        ) : (
          conversationMessages.map((msg) => (
            <div
              key={msg.id}
              className={`floating-chat-message ${msg.user_id === user?.id ? 'other' : 'self'}`}
            >
              <div className="message-content">{msg.content}</div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="floating-chat-input">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          disabled={sending}
        />
        <button
          className="floating-chat-send"
          onClick={handleSend}
          disabled={!inputValue.trim() || sending}
        >
          <IoSend />
        </button>
      </div>
    </div>
  );
};

export default FloatingChat;
